import React, { useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import { Button } from '@zillow/constellation';

import PropertiesActions from 'app/shared/actions/PropertiesActions';
import { PropertiesSelectors } from 'app/shared/selectors';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import propertyUtils from 'app/shared/utils/propertyUtils';
import { RMWContext } from 'app/shared/providers/RMWContext';
import CustomerSupportErrorMessage from '../CustomerSupportErrorMessage';

const {
    PROPERTY_TYPES: { COMMUNITY },
    PROPERTY_CODES_BY_TYPE,
} = PropertyConstants;
const { formatAddress } = propertyUtils;

const propTypes = {
    isSubmitDisabled: PropTypes.bool.isRequired,
    verifyCanSubmitForm: PropTypes.func.isRequired,
    onStartSubmit: PropTypes.func.isRequired,
    onNewError: PropTypes.func.isRequired,
    onModalClose: PropTypes.func.isRequired,
    listing: PropTypes.object.isRequired,
};

const EditButton = ({
    isSubmitDisabled,
    onStartSubmit,
    onNewError,
    verifyCanSubmitForm,
    onModalClose,
    listing,
}) => {
    const { paths } = useContext(RMWContext);
    const dispatch = useDispatch();
    const currentProperty = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const { listingAlias, propertyTypeCode } = currentProperty;

    const remapErrorMessage = ({ data, message }) => {
        const matchDuplicatePropertyError = 'Another property with that address already exists.';
        const matchCometFeatureError = 'Please contact customer support if there is an error with the address. You' +
            ' can also add a new property or unit.';

        switch (message.toLowerCase()) {
            // comet features enabled
            case matchCometFeatureError.toLowerCase():
                return <CustomerSupportErrorMessage modalClose={onModalClose} />;

            case matchDuplicatePropertyError.toLowerCase():
                return (
                    <React.Fragment>
                        Another property with that address already exists.&nbsp;
                        <Link
                            onClick={() => {
                                window.router.transitionTo(
                                    `${paths.base}/properties/${data.duplicateListingId}/listing`
                                );
                                onModalClose();
                            }}
                        >
                            View that property.
                        </Link>
                    </React.Fragment>
                );

            default:
                return message;
        }
    };

    const handleEditPropertySubmit = async () => {
        const canSubmitForm = await verifyCanSubmitForm();

        const {
            address,
            unit,
            propertyType,
            numUnits,
        } = listing;

        if (canSubmitForm) {
            const addressNeedsUpdate = formatAddress(currentProperty).formattedCompleteAddress !== address;
            const unitNeedsUpdate = currentProperty.unit !== unit;
            const numUnitsNeedsUpdate = currentProperty.numUnits !== numUnits;

            const changingPropertyTypeCode = PROPERTY_CODES_BY_TYPE[propertyType];
            const propertyTypeNeedsUpdate = changingPropertyTypeCode !== propertyTypeCode;
            const isCommunityType = propertyType === COMMUNITY;

            if (addressNeedsUpdate || unitNeedsUpdate || propertyTypeNeedsUpdate || numUnitsNeedsUpdate) {
                try {
                    onStartSubmit();
                    await dispatch(PropertiesActions.savePropertyInfo({
                        fullAddress: address,
                        ...(!isCommunityType && { unit }),
                        numUnits,
                        propertyId: listingAlias,
                        propertyTypeCode: changingPropertyTypeCode,
                    }));

                    window.location.reload();
                } catch (errorRMWModel) {
                    onNewError('address', remapErrorMessage(errorRMWModel));
                }
            } else {
                onModalClose();
            }
        }
    };

    return (
        <Button
            fluid={true}
            buttonType="primary"
            onClick={handleEditPropertySubmit}
            disabled={isSubmitDisabled}
        >
            Save changes
        </Button>
    );
};

EditButton.propTypes = propTypes;

export default EditButton;
